import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import {
  listEligibleTouchPanelDevices,
  assignPanelToDevice,
  unassignDevice,
} from '../../../services/touchPanelAssignmentService';

function CompatChip({ status }) {
  if (status === 'compatible') return <Chip size="small" color="success" label="Compatible" />;
  if (status === 'unknown') return <Chip size="small" color="warning" label="Unknown" />;
  return <Chip size="small" color="error" label="Incompatible" />;
}

function OnlineChip({ online }) {
  if (online === 'online') return <Chip size="small" color="success" variant="outlined" label="Online" />;
  if (online === 'degraded') return <Chip size="small" color="warning" variant="outlined" label="Degraded" />;
  if (online === 'offline') return <Chip size="small" color="default" variant="outlined" label="Offline" />;
  return <Chip size="small" variant="outlined" label="Unknown" />;
}

/**
 * Assign / replace / unassign eligible Edge Agent devices to a Touch Panel.
 */
export default function AssignDevicesDialog({
  open,
  onClose,
  tenantId,
  userId,
  panel,
  onChanged,
}) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [allowIncompatible, setAllowIncompatible] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !open) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listEligibleTouchPanelDevices(tenantId, {
        panelId: panel?.id,
        includeIncompatible: true,
        search: '',
      });
      setDevices(rows);
    } catch (err) {
      console.error('[AssignDevices] load failed', err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to load devices'
      );
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, open, panel?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) =>
      [d.name, d.deviceId, d.model, d.firmwareVersion]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [devices, search]);

  const doAssign = async (device, { replaceExisting = false } = {}) => {
    if (!tenantId || !panel?.id || busy) return;
    setBusy(true);
    setError(null);
    try {
      await assignPanelToDevice(tenantId, {
        panelId: panel.id,
        deviceId: device.deviceId,
        userId,
        deviceView: device,
        allowIncompatible,
        replaceExisting,
      });
      setReplaceTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      if (err.code === 'DEVICE_ALREADY_ASSIGNED') {
        setReplaceTarget({ device, existing: err.existingAssignment });
      } else {
        console.error('[AssignDevices] assign failed', err);
        setError(err.message || 'Assign failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const doUnassign = async (device) => {
    if (!tenantId || busy) return;
    setBusy(true);
    setError(null);
    try {
      await unassignDevice(tenantId, device.deviceId, userId);
      await load();
      onChanged?.();
    } catch (err) {
      console.error('[AssignDevices] unassign failed', err);
      setError(err.message || 'Unassign failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>Assign Devices — {panel?.name || 'Panel'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Eligible devices are Edge Agents (ESP32 / panel form-factor). Algo/phone inventory
              devices are not listed here.
            </Typography>

            {error ? (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                size="small"
                fullWidth
                label="Search devices"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={allowIncompatible}
                    onChange={(e) => setAllowIncompatible(e.target.checked)}
                    color="warning"
                  />
                }
                label="Dev: allow incompatible"
              />
            </Stack>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Device ID</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Display</TableCell>
                    <TableCell>Firmware</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Compatibility</TableCell>
                    <TableCell>Current Panel</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography color="text.secondary">
                          No eligible Edge Agents found for this tenant.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((d) => {
                      const onThisPanel =
                        d.currentAssignment?.panelId === panel?.id && d.currentAssignment?.enabled;
                      const blocked =
                        d.compatibilityStatus === 'incompatible' && !allowIncompatible;
                      const display =
                        d.displayWidth && d.displayHeight
                          ? `${d.displayWidth}×${d.displayHeight} ${d.orientation || ''}`
                          : '—';
                      return (
                        <TableRow key={d.deviceId} hover>
                          <TableCell>{d.name}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {d.deviceId}
                            </Typography>
                          </TableCell>
                          <TableCell>{d.model || '—'}</TableCell>
                          <TableCell>{display}</TableCell>
                          <TableCell>{d.firmwareVersion || '—'}</TableCell>
                          <TableCell>
                            <OnlineChip online={d.online} />
                          </TableCell>
                          <TableCell>
                            <CompatChip status={d.compatibilityStatus} />
                          </TableCell>
                          <TableCell>
                            {d.currentAssignment?.enabled
                              ? d.currentAssignment.panelName || d.currentAssignment.panelId
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {onThisPanel ? (
                                <Button
                                  size="small"
                                  color="warning"
                                  disabled={busy}
                                  onClick={() => doUnassign(d)}
                                >
                                  Unassign
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={busy || blocked}
                                  onClick={() => doAssign(d)}
                                >
                                  Assign
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        isOpen={!!replaceTarget}
        title="Replace device assignment?"
        message={
          replaceTarget
            ? `“${replaceTarget.device.name}” is assigned to another panel (${
                replaceTarget.existing?.panelId || 'unknown'
              }). Replace with “${panel?.name}”?`
            : ''
        }
        onCancel={() => setReplaceTarget(null)}
        onConfirm={() => doAssign(replaceTarget.device, { replaceExisting: true })}
      />
    </>
  );
}
